import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useAiLogs, deriveLogKind, type LogSource, type LogKind } from '@/features/ai-logs/api';
import { useUsersList } from '@/features/users/api';
import {
  AttemptsIndicator,
  LogDetailDialog,
  formatCost,
  statusLabel,
  statusTone,
  totalLogCost,
} from '@/features/ai-logs/LogDetailDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Segmented } from '@/components/ui/Segmented';
import type { AiGenerationStatus } from '@honeycomb/shared';

const KIND_TONE: Record<LogKind, 'neutral' | 'honey' | 'sage' | 'danger'> = {
  generate: 'neutral',
  edit: 'honey',
  wizard: 'sage',
  import: 'danger',
};

const KIND_LABEL: Record<LogKind, string> = {
  generate: 'Generate',
  edit: 'Edit',
  wizard: 'Wizard',
  import: 'Import',
};

export const Route = createFileRoute('/_authed/ai/logs/')({
  component: AiLogsPage,
});

const STATUS_OPTIONS: Array<{ value: AiGenerationStatus | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'success', label: 'Success' },
  { value: 'invalid_json', label: 'Invalid JSON' },
  { value: 'validation_failed', label: 'Validation failed' },
  { value: 'llm_error', label: 'LLM error' },
  { value: 'rate_limited', label: 'Rate limited' },
  { value: 'image_failed', label: 'Image failed' },
];

const SOURCE_OPTIONS: Array<{ value: LogSource | ''; label: string }> = [
  { value: '', label: 'All' },
  { value: 'edit', label: 'Edit' },
  { value: 'wizard', label: 'Wizard' },
  { value: 'import', label: 'Import' },
];

const LIMIT = 50;

function AiLogsPage() {
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState<AiGenerationStatus | ''>('');
  const [source, setSource] = useState<LogSource | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);

  const [selectedLogId, setSelectedLogId] = useState<string | null>(null);

  const params = {
    userId: userId.trim() || undefined,
    status: status || undefined,
    source: source || undefined,
    from: from || undefined,
    to: to || undefined,
    limit: LIMIT,
    offset,
  };

  const { data, isLoading } = useAiLogs(params);
  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;

  const { data: usersData } = useUsersList({ limit: 200 });
  const userNameById = new Map<string, string>();
  for (const u of usersData?.users ?? []) {
    userNameById.set(u.id, u.displayName);
  }

  const handleClear = () => {
    setUserId('');
    setStatus('');
    setSource('');
    setFrom('');
    setTo('');
    setOffset(0);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">AI logs</h1>
        {!isLoading && (
          <p className="text-ink-soft text-sm mt-1">{total} generation{total === 1 ? '' : 's'}</p>
        )}
      </header>

      {/* Filters */}
      <div className="mb-4 flex flex-col sm:flex-row sm:flex-wrap sm:items-end gap-3">
        <div className="w-full sm:w-56">
          <Input
            label="User ID"
            placeholder="Exact user ID"
            value={userId}
            onChange={(e) => { setUserId(e.target.value); setOffset(0); }}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-ink-soft">Status</div>
          <Segmented
            value={status}
            onChange={(v) => { setStatus(v as AiGenerationStatus | ''); setOffset(0); }}
            options={STATUS_OPTIONS}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <div className="text-sm text-ink-soft">Source</div>
          <Segmented
            value={source}
            onChange={(v) => { setSource(v as LogSource | ''); setOffset(0); }}
            options={SOURCE_OPTIONS}
          />
        </div>
        <div className="w-full sm:w-auto flex flex-col gap-1.5">
          <label className="text-sm text-ink-soft">From</label>
          <input
            type="date"
            value={from}
            onChange={(e) => { setFrom(e.target.value); setOffset(0); }}
            className="rounded-lg bg-paper px-3 py-2 text-sm text-ink border border-[var(--color-hairline)] outline-none focus:border-honey focus:ring-2 focus:ring-honey-pale"
          />
        </div>
        <div className="w-full sm:w-auto flex flex-col gap-1.5">
          <label className="text-sm text-ink-soft">To</label>
          <input
            type="date"
            value={to}
            onChange={(e) => { setTo(e.target.value); setOffset(0); }}
            className="rounded-lg bg-paper px-3 py-2 text-sm text-ink border border-[var(--color-hairline)] outline-none focus:border-honey focus:ring-2 focus:ring-honey-pale"
          />
        </div>
        <Button variant="ghost" onClick={handleClear}>Clear</Button>
      </div>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="p-6 text-ink-soft">No logs found.</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr
                    className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                    style={{ borderColor: 'var(--color-hairline)' }}
                  >
                    <th className="px-4 py-3">Created at</th>
                    <th className="px-4 py-3">User</th>
                    <th className="px-4 py-3">Kind</th>
                    <th className="px-4 py-3">Model</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Attempts</th>
                    <th className="px-4 py-3">Duration</th>
                    <th className="px-4 py-3">Tokens in/out</th>
                    <th className="px-4 py-3 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      className="border-b last:border-b-0 hover:bg-cream-deep/60 cursor-pointer"
                      style={{ borderColor: 'var(--color-hairline)' }}
                      onClick={() => setSelectedLogId(log.id)}
                    >
                      <td className="px-4 py-3 font-mono text-xs text-ink-soft">
                        {new Date(log.createdAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        {userNameById.has(log.userId) && (
                          <div className="text-ink">{userNameById.get(log.userId)}</div>
                        )}
                        <div className="font-mono text-ink-soft">
                          {log.userId.slice(0, 12)}…
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {(() => { const k = deriveLogKind(log); return <Badge tone={KIND_TONE[k]}>{KIND_LABEL[k]}</Badge>; })()}
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-soft">
                        {log.model}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Badge tone={statusTone(log.status)}>{statusLabel(log.status)}</Badge>
                          {log.appliedAt && (
                            <span title={`Applied at ${new Date(log.appliedAt).toLocaleString()}`}>
                              <CheckCircle2 size={14} className="text-sage shrink-0" />
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-soft">
                        <AttemptsIndicator attempts={log.attempts} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-soft">
                        {log.durationMs}ms
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-soft">
                        {log.tokensInput ?? '—'} / {log.tokensOutput ?? '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-soft text-right">
                        {formatCost(totalLogCost(log))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <ul className="md:hidden divide-y" style={{ borderColor: 'var(--color-hairline)' }}>
              {logs.map((log) => (
                <li key={log.id}>
                  <button
                    type="button"
                    className="w-full text-left p-4 hover:bg-cream-deep/60"
                    onClick={() => setSelectedLogId(log.id)}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="flex items-center gap-2">
                        {(() => { const k = deriveLogKind(log); return <Badge tone={KIND_TONE[k]}>{KIND_LABEL[k]}</Badge>; })()}
                        <Badge tone={statusTone(log.status)}>{statusLabel(log.status)}</Badge>
                        <AttemptsIndicator attempts={log.attempts} hideOne />
                        {log.appliedAt && (
                          <span title={`Applied at ${new Date(log.appliedAt).toLocaleString()}`}>
                            <CheckCircle2 size={14} className="text-sage shrink-0" />
                          </span>
                        )}
                      </div>
                      <span className="font-mono text-xs text-ink-soft">{formatCost(totalLogCost(log))}</span>
                    </div>
                    <div className="text-xs text-ink-soft font-mono mb-0.5">
                      {new Date(log.createdAt).toLocaleString()}
                    </div>
                    <div className="text-xs text-ink-soft truncate mb-0.5">{log.model}</div>
                    <div className="flex gap-3 text-xs text-ink-faint font-mono">
                      <span>{log.durationMs}ms</span>
                      <span>{log.tokensInput ?? '—'} / {log.tokensOutput ?? '—'} tokens</span>
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {/* Pagination */}
      {total > LIMIT && (
        <div className="mt-4 flex items-center gap-3">
          <Button
            variant="ghost"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
          >
            Prev
          </Button>
          <span className="text-sm text-ink-soft">
            {offset + 1}–{Math.min(offset + LIMIT, total)} of {total}
          </span>
          <Button
            variant="ghost"
            disabled={offset + LIMIT >= total}
            onClick={() => setOffset(offset + LIMIT)}
          >
            Next
          </Button>
        </div>
      )}

      {selectedLogId && (
        <LogDetailDialog
          logId={selectedLogId}
          onClose={() => setSelectedLogId(null)}
          onSelectLog={(id) => setSelectedLogId(id)}
        />
      )}
    </div>
  );
}
